import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import {
    AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon,
    ArchiveOutlined as ArchiveOutlinedIcon,
    ArrowBack as ArrowBackIcon,
    DeleteOutlined as DeleteOutlinedIcon,
} from '@mui/icons-material';

import { Link } from '../../../../components/Link';
import { useConfirmDialogContext } from '../../../../hooks/Contexts/ConfirmDialogContext';
import { useInfoMessage } from '../../../../hooks/useInfoMessage';
import { useUserContext } from '../../../../hooks/Contexts/UserContext';
import ApiService from '../../../../ApiService';
import Block from '../../../../components/Block';
import Column from '../../../../components/Column';
import PlatformLogo from '../../../../components/Logo/PlatformLogo';
import Row from '../../../../components/Row';
import VerticalDivider from '../../../../components/Divider/VerticalDivider';

import { Campaign, Platform } from '../../../../types/Campaign';
import { useCampaignPageContext } from '../../../../hooks/Contexts/Pages/CampaignPageContext';
import CampaignActionMenu from './Menu/CampaignActionMenu';
import CampaignStatus from '../../../campaignManager/CampaignStatusComponent';

interface CampaignPageHeaderProps {
    campaign: Campaign;
}

const CampaignPageHeader: FC<CampaignPageHeaderProps> = ({ campaign }) => {
    const navigate = useNavigate();
    const { showConfirm, hideConfirm } = useConfirmDialogContext();
    const { showInfoMessage } = useInfoMessage();

    const { setCampaign } = useCampaignPageContext();
    const { userContext } = useUserContext();

    const handleOnArchive = (campaign: Campaign): void => {
        showConfirm({
            title: 'Are you sure you want to archive this campaign?',
            onOk: () => {
                hideConfirm();

                if (campaign && campaign.id) {
                    ApiService.archiveCampaign(campaign.id)
                        .then(({ data }) => {
                            setCampaign({ ...data });

                            showInfoMessage('success', 'Campaign has been archived.');
                        })
                        .catch((error) => {
                            showInfoMessage('error', error.message);
                        });
                }
            },
            okText: 'Archive Campaign',
            okColor: 'error',
            okIcon: <ArchiveOutlinedIcon />,
        });
    };

    const handleOnDelete = (campaign: Campaign): void => {
        showConfirm({
            title: 'Are you sure you want to delete this campaign?',
            text: 'This cannot be undone.',
            onOk: () => {
                hideConfirm();

                if (campaign && campaign.id) {
                    ApiService.deleteCampaign(campaign.id)
                        .then(() => {
                            showInfoMessage('success', 'Campaign has been deleted.');

                            setTimeout(() => {
                                navigate('/campaignManager');
                            }, 10);
                        })
                        .catch((error) => {
                            showInfoMessage('error', error.message);
                        });
                }
            },
            okText: 'Delete Campaign',
            okColor: 'error',
            okIcon: <DeleteOutlinedIcon />,
        });
    };

    const handleViewPerformance = useCallback(() => {
        navigate('/reporting/all-media', {
            state: {
                reportingCriteria: {
                    agencyIds: [],
                    agencyNames: [],
                    oemIds: [],
                    oemNames: [],
                    oemProgramIds: [],
                    oemProgramNames: [],
                    dealerIds: [campaign.dealerId],
                    dealerNames: [campaign.dealer?.dealerName],
                    startDate: campaign.startDate,
                    endDate: campaign.endDate,
                    mediaType: '',
                    strategyType: '',
                },
                performanceTab: 1,
            },
            replace: false,
        });
    }, [campaign, navigate]);

    return (
        <Column gap={1}>
            <Block>
                <Column>
                    <Column gap={1}>
                        <Row>
                            <Link
                                href="/campaignManager"
                                variant="text"
                                sx={{ color: 'text.primary' }}
                                startIcon={<ArrowBackIcon sx={{ fontSize: '16px' }} />}
                            >
                                Back to Campaigns
                            </Link>
                        </Row>

                        <Row justifyContent="space-between">
                            <Row gap={1} alignItems="center">
                                <Typography variant="h6">{campaign.campaignName}</Typography>

                                <VerticalDivider />

                                <PlatformLogo size="Small" platform={campaign.platform} />

                                <VerticalDivider />

                                <CampaignStatus deliveryStatus={campaign.deliveryStatus} />
                            </Row>

                            <Row gap={1} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="medium"
                                    onClick={handleViewPerformance}
                                >
                                    View Performance
                                </Button>

                                <Button
                                    disabled={campaign.platform === Platform.DV360}
                                    href={`/campaign/v2/${campaign.id}`}
                                    variant="contained"
                                    color="primary"
                                    size="medium"
                                >
                                    Modify
                                </Button>

                                <CampaignActionMenu
                                    campaign={campaign}
                                    actions={[
                                        {
                                            label: 'Archive',
                                            callback: handleOnArchive,
                                            disabled: campaign.deliveryStatusId === 'ARCHIVED',
                                        },
                                        ...(userContext.isAdmin()
                                            ? [
                                                  {
                                                      label: 'Delete',
                                                      callback: handleOnDelete,
                                                      color: 'error' as const,
                                                      tooltip:
                                                          campaign.totalSpend > 0
                                                              ? 'Campaigns with spend cannot be deleted'
                                                              : 'Delete Campaign (Visible to Admins Only)',
                                                      disabled: campaign.totalSpend > 0,
                                                      icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
                                                  },
                                              ]
                                            : []),
                                    ]}
                                />
                            </Row>
                        </Row>
                    </Column>
                </Column>
            </Block>
        </Column>
    );
};

export default CampaignPageHeader;
